import { calculatePrice, isInbound } from "../../../Lib/Utils/validationUtil";
import { isNolAncillary } from "../../../Lib/Utils/nolCard";
import { AdmissionStatusEnum, AncillaryStatusEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { ICarriedOverAddon, ICarriedOverAddons } from "../../../Orchestrator/Types/Interface/IAddOns";
import { IBookedOffer, IBooking } from "../../../Orchestrator/Types/Index";
import { ITrip } from "../../../Orchestrator/Types/Interface/ITrip";

const ACTIVE_ANCILLARY_STATUSES = new Set<string>([
  AncillaryStatusEnum.EXCHANGE_ONGOING,
  AncillaryStatusEnum.FULFILLED,
  AncillaryStatusEnum.CONFIRMED,
]);

/**
 * Extract existing ancillaries from EXCHANGE_ONGOING offers in a booking.
 * Used during upgrade-to-premium to show carried-over addons as read-only.
 */
export function extractCarriedOverAddons(booking: IBooking): ICarriedOverAddons {
  const result: ICarriedOverAddons = { outbound: [], inbound: [] };

  const exchangeOffers = (booking.bookedOffers || []).filter(
    (offer: IBookedOffer) =>
      offer.admissions?.some((a) => a.status === AdmissionStatusEnum.EXCHANGE_ONGOING),
  );

  // Pre-flatten products across every booked offer. Post-exchange the product
  // referenced by an ancillary may live on a sibling offer (e.g. moved with the
  // PREBOOKED upgrade offer); without this fallback, addonName silently
  // degrades to anc.type and the modal lock-by-label breaks.
  const allProducts = (booking.bookedOffers || []).flatMap(
    (o: IBookedOffer) => o.products || [],
  );

  for (const offer of exchangeOffers) {
    const trip = (booking.trips || []).find(
      (t: ITrip) => t.id === offer.tripCoverage?.coveredTripId,
    );
    if (!trip) continue;

    const direction = isInbound(trip.direction) ? "inbound" : "outbound";

    for (const anc of (offer.ancillaries || [])) {
      if (!ACTIVE_ANCILLARY_STATUSES.has(anc.status as string)) continue;
      // NOL passes belong to the End-to-end Journey flow, not the Extras
      // carry-over — a Day Pass must not surface as a carried-over addon on
      // upgrade-to-premium. (Products may live on a sibling offer post-exchange,
      // so match against the flattened product list.)
      if (isNolAncillary(allProducts, anc)) continue;

      const productId = anc.products?.[0]?.productId;
      const product =
        offer.products?.find((p: any) => p.id === productId) ??
        allProducts.find((p: any) => p.id === productId);
      const passenger = (booking.passengers || []).find(
        (p: any) => p.id === anc.passengerIds?.[0],
      );

      const addon: ICarriedOverAddon = {
        passengerId: anc.passengerIds?.[0] || "",
        passengerName: passenger
          ? `${passenger.detail?.firstName || ""} ${passenger.detail?.lastName || ""}`.trim()
          : "",
        addonName: (product as any)?.description || anc.type || "Add-on",
        category: anc.type || (product as any)?.description || "Add-on",
        amount: calculatePrice(anc.price),
        code: (product as any)?.code,
      };
      result[direction].push(addon);
    }
  }

  return result;
}
